import { useEffect, useState, useCallback } from "react";
import api, { inr } from "../../lib/api";
import { PageHeader, Card, Loading, Empty, StatusBadge, inputCls } from "../../components/admin/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Search, User } from "lucide-react";

export default function Students() {
  const [rows, setRows] = useState(null);
  const [search, setSearch] = useState("");
  const [sel, setSel] = useState(null);

  const load = useCallback(() => {
    const p = new URLSearchParams(); if (search) p.set("search", search);
    setRows(null);
    api.get(`/students?${p}`).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [search]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  return (
    <div>
      <PageHeader title="Students" subtitle="Directory of all registered students." />
      <Card className="p-4 mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input className={`${inputCls} pl-10`} placeholder="Search student, parent, mobile or email" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="student-search" />
        </div>
      </Card>
      <Card>
        {rows === null ? <Loading /> : rows.length === 0 ? <Empty label="No students yet" /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wider text-slate-500">
                {["Student", "Parent / Mobile", "School", "Std / Div", "Registrations"].map((h) => <th key={h} className="px-5 py-3 font-semibold">{h}</th>)}
              </tr></thead>
              <tbody>
                {rows.map((s) => (
                  <tr key={s.id} className="border-b border-slate-50 hover:bg-slate-50/60 cursor-pointer" onClick={() => setSel(s)} data-testid="student-row">
                    <td className="px-5 py-3.5 font-medium text-slate-800">{s.student_name}</td>
                    <td className="px-5 py-3.5 text-slate-600">{s.parent_name}<div className="text-xs text-slate-400 font-mono">{s.mobile}</div></td>
                    <td className="px-5 py-3.5 text-slate-600">{s.school_name || "—"}</td>
                    <td className="px-5 py-3.5 text-slate-600">{s.standard || "—"} {s.division}</td>
                    <td className="px-5 py-3.5"><span className="font-mono text-slate-700">{s.registrations?.length || 0}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><User className="w-5 h-5 text-blue-600" /> {sel?.student_name}</DialogTitle></DialogHeader>
          {sel && (
            <div className="space-y-4 max-h-[65vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Info label="DOB" value={sel.dob} /><Info label="Gender" value={sel.gender} />
                <Info label="Standard" value={sel.standard} /><Info label="Division" value={sel.division} />
                <Info label="Roll No" value={sel.roll_number} /><Info label="School" value={sel.school_name} />
                <Info label="Parent" value={sel.parent_name} /><Info label="Mobile" value={sel.mobile} />
                <Info label="Email" value={sel.email} /><Info label="City" value={sel.city} />
              </div>
              {sel.custom_data && Object.keys(sel.custom_data).length > 0 && (
                <div className="grid grid-cols-2 gap-3 text-sm border-t border-slate-100 pt-3">
                  {Object.entries(sel.custom_data).map(([k, val]) => <Info key={k} label={k} value={val} />)}
                </div>
              )}
              <div>
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Registrations</div>
                <div className="space-y-2">
                  {(sel.registrations || []).map((r) => (
                    <div key={r.id} className="border border-slate-200 rounded-xl p-3 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-blue-600 text-xs">{r.registration_id}</span>
                        <StatusBadge status={r.payment_status} />
                      </div>
                      <div className="text-slate-600 mt-1">{r.program_name} · {r.level_name} · {r.batch_name} — {inr(r.amount)}</div>
                    </div>
                  ))}
                  {(!sel.registrations || sel.registrations.length === 0) && <p className="text-sm text-slate-400">No registrations.</p>}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
function Info({ label, value }) {
  return <div><div className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{label}</div><div className="text-slate-800">{value || "—"}</div></div>;
}
